export const DEFAULT_BRAND_KEY = 'chargewhisper';

const sharedImages = {
  overview: '/img/csms-overview.svg',
  chargePoints: '/img/charge-points.svg',
  sessions: '/img/sessions.svg',
  payments: '/img/payments.svg',
  diagnostics: '/img/diagnostics.svg',
  systemsChargePoints: '/img/systems-charge-points.svg',
  systemsPayments: '/img/systems-payments.svg',
  systemsIdentity: '/img/systems-identity.svg',
  standardOcpp: '/img/standard-ocpp.svg',
  standardKeycloak: '/img/standard-keycloak.svg',
  standardMtls: '/img/standard-mtls.svg',
  standardRest: '/img/standard-rest.svg',
  productSecureAccess: '/img/product-secure-access.svg',
  productDiagnostics: '/img/product-diagnostics.svg',
  productNetworkProtection: '/img/product-network-protection.svg',
};

export const BRAND_CONTENT = {
  chargewhisper: {
    key: 'chargewhisper',
    locale: 'en',
    htmlLang: 'en',
    name: 'ChargeWhisper',
    shortName: 'ChargeWhisper',
    mark: 'C',
    domain: 'chargewhisper.com',
    tagline: 'AI-assisted diagnostics for OCPP charge points.',
    titleBase: 'ChargeWhisper - AI charge point diagnostics',
    footer: 'ChargeWhisper. OCPP charge point diagnostics and operations.',
    seo: {
      title: 'ChargeWhisper - AI charge point diagnostics',
      description:
        'AI-assisted diagnostics and support for EV charge point operators using OCPP, message logs, health summaries, and operational workflows.',
      twitterDescription: 'AI-assisted diagnostics and support for OCPP charge points.',
    },
    nav: {
      product: 'Product',
      howItWorks: 'How it works',
      technology: 'Technology',
      contact: 'Contact',
      requestDemo: 'Request demo',
      login: 'Login',
      driverApp: 'Driver app',
    },
    cta: {
      eyebrow: 'Ready to launch?',
      heading: 'Start operating your charging network with ChargeWhisper',
      subheading:
        'Log in if you already have access, or request a demo to see how ChargeWhisper connects charge points, payments, diagnostics, and driver flows.',
      loginAsCpo: 'Login as CPO',
      requestDemo: 'Request a demo',
      driverLink: 'Driver? Open driver app',
    },
    contact: {
      email: 'hello@chargewhisper.com',
      subject: 'ChargeWhisper - Demo or access request',
    },
    diagram: {
      ariaLabel:
        'ChargeWhisper ecosystem: charge points connect to the ChargeWhisper backend over OCPP; the backend exposes REST APIs to operator and driver frontends and to payment systems.',
      chargePoints: 'Charge Points',
      drivers: 'Drivers',
      driverSub: 'Scan QR / pay / charge',
      backend: 'ChargeWhisper Backend',
      operatorFrontend: 'Operator Frontend',
      paymentSystems: 'Payment Systems',
      driverApp: 'Driver App',
      qrLanding: 'QR landing page',
    },
    pages: {
      home: {
        title: undefined,
        eyebrow: 'ChargeWhisper',
        heroTitle: 'One control layer for your EV charging network',
        heroLead:
          'ChargeWhisper connects charge points, operators, payments, diagnostics, and driver flows in one central platform. It speaks OCPP with charging hardware, exposes APIs for integrations, and gives operators the visibility they need to run reliable charging infrastructure.',
        heroImage: sharedImages.overview,
        heroImageAlt: 'ChargeWhisper connecting charge points, payments, operators, and drivers',
        pillarsHeading: 'From plug to payment, in one platform',
        pillarsSubhead: 'Four pillars that cover the full lifecycle of every EV charging session.',
        pillars: [
          {
            title: 'Charge point management',
            description: 'Register and monitor stations, real-time status, health summaries, and remote OCPP commands from one dashboard.',
            image: sharedImages.chargePoints,
            alt: 'Illustration of EV charging stations with a connector cable',
            link: { to: '/product#management', label: 'See feature' },
          },
          {
            title: 'Charging sessions and reporting',
            description: 'Sessions report, session detail, and meter values for billing, analytics, and compliance.',
            image: sharedImages.sessions,
            alt: 'Illustration of a charging sessions chart in kilowatt-hours',
            link: { to: '/product#sessions', label: 'See feature' },
          },
          {
            title: 'Payment integration',
            description: 'Map terminals to charge points, start and stop transactions via payment, and offer drivers a no-account QR flow.',
            image: sharedImages.payments,
            alt: 'Illustration of a payment terminal next to a QR code',
            link: { to: '/product#payments', label: 'See feature' },
          },
          {
            title: 'Diagnostics and maintenance',
            description: 'OCPP message logs, health visibility, eviction events, and supported diagnostic commands per charge point.',
            image: sharedImages.diagnostics,
            alt: 'Illustration of an OCPP message log console',
            link: { to: '/product#diagnostics', label: 'See feature' },
          },
        ],
        carousel: {
          slideLabel: 'Slide',
          summarySlide: 'See all features',
          previous: 'Previous',
          next: 'Next',
        },
        pillarsSummaryTitle: 'See the full picture',
        pillarsSummaryDescription: 'Dashboard, sessions, payments, diagnostics, security - explore every capability in one place.',
        pillarsSummaryCta: 'See all features',
        systemsHeading: 'Connect every part of your charging operation',
        systemsSubhead:
          'ChargeWhisper sits at the centre between your stations, your team, your payments, and your drivers - so every system speaks the same language.',
        systemsLink: 'See the full architecture and flows',
        systems: [
          {
            title: 'Talk to every charge point',
            body: 'Stations connect over OCPP on WebSocket. ChargeWhisper keeps a live session per charge point, accepts boot, status, and transaction events, and pushes remote commands like start, stop, and reset. Run with mTLS in production so only your hardware can connect.',
            image: sharedImages.systemsChargePoints,
            alt: 'Charge point exchanging OCPP messages with the ChargeWhisper backend',
          },
          {
            title: 'Plug into the payment provider you use',
            body: "Map payment terminals to chargers, expose a no-account QR flow to drivers, and let any compliant payment app start and stop sessions through public APIs. Your terminal vendor does not need to know anything about OCPP.",
            image: sharedImages.systemsPayments,
            alt: 'Driver scans a QR, the payment app charges them, ChargeWhisper starts the session',
          },
          {
            title: 'One identity for your team',
            body: 'Keycloak handles authentication, roles, and user admin. Operators sign in once and reach the dashboard, reports, and diagnostics with the right permissions - and you can federate Keycloak with the IdP you already run.',
            image: sharedImages.systemsIdentity,
            alt: 'Three team members signing in once with Keycloak roles',
          },
        ],
        standardsHeading: 'Built on open standards',
        standardsLink: 'More on the technology stack',
        standards: [
          {
            title: 'OCPP',
            description: 'Protocol-native communication between ChargeWhisper and every connected charge point.',
            image: sharedImages.standardOcpp,
            alt: 'OCPP protocol messages connecting a charge point to ChargeWhisper',
          },
          {
            title: 'Keycloak',
            description: 'Central authentication, roles, and operator access control.',
            image: sharedImages.standardKeycloak,
            alt: 'Identity key connecting users and roles',
          },
          {
            title: 'mTLS',
            description: 'Optional certificate-based protection for production charge point connections.',
            image: sharedImages.standardMtls,
            alt: 'Certificate shield protecting a charging network connection',
          },
          {
            title: 'REST APIs',
            description: 'Clean integration layer for operators, payment systems, and driver flows.',
            image: sharedImages.standardRest,
            alt: 'REST API nodes exchanging integration requests',
          },
        ],
        audiencesHeading: 'Who runs on ChargeWhisper',
        audiencesSubhead: 'Built for the teams that operate, integrate, maintain, and use EV charging networks.',
        audiences: [
          {
            label: 'CPO',
            title: 'Charge point operators',
            description: 'Run the network from one place: stations, live status, sessions, meter values, and remote commands.',
          },
          {
            label: 'SITE',
            title: 'Site owners and fleets',
            description: 'Track uptime, charger health, energy delivered, and payment setup across locations.',
          },
          {
            label: 'PAY',
            title: 'Payment and mobility providers',
            description: 'Connect payment apps through public APIs for QR flow, start/stop, and session status.',
          },
          {
            label: 'OPS',
            title: 'Maintenance teams',
            description: 'Troubleshoot stations with health summaries, OCPP message logs, and diagnostic commands.',
          },
          {
            label: 'ADM',
            title: 'Administrators',
            description: 'Manage users, roles, operator access, terminal mappings, and Keycloak-backed permissions.',
          },
          {
            label: 'DRV',
            title: 'Drivers',
            description: 'Scan, pay, and charge without creating a ChargeWhisper account or learning a new app.',
          },
        ],
        driverHeading: 'Driver flow at a glance',
        driverSubhead: 'A simple connected journey from the station QR code to an active charging session.',
        driverLink: 'See the full driver flow',
        driverSteps: [
          {
            number: '01',
            title: 'Scan',
            description: 'The driver scans the QR code on the station and opens the driver app with the charge point id.',
          },
          {
            number: '02',
            title: 'Pay',
            description: 'The configured payment app shows available connectors and starts the session after payment.',
          },
          {
            number: '03',
            title: 'Charge',
            description: 'The driver returns to a status page while ChargeWhisper tracks the session and energy delivered.',
          },
        ],
      },
      product: {
        title: 'Product',
        heroLead:
          'Everything ChargeWhisper gives you to operate an EV charging network: management, sessions, payments, diagnostics, and security.',
        introEyebrow: 'Product capabilities',
        introTitle: 'Everything needed to operate a charging network',
        introBody:
          'ChargeWhisper brings network operations, charging reports, payment integrations, diagnostics, and secure access into one operator-facing product. It connects stations, teams, payment providers, and driver journeys through clear product workflows.',
        navAriaLabel: 'Product capabilities',
        sections: [
          {
            id: 'management',
            eyebrow: 'Network operations',
            title: 'Control every charge point from one place',
            description: 'Register stations, monitor live status, inspect connector availability, and send remote station commands without switching tools.',
            image: sharedImages.chargePoints,
            alt: 'Illustration of managed EV charge points',
            highlights: [
              'Charge point registry with EVSEs and connectors',
              'Online, offline, available, occupied, faulted, and unavailable state',
              'Remote commands for start, stop, unlock, reset, variables, availability, and network profiles',
            ],
          },
          {
            id: 'sessions',
            eyebrow: 'Billing and analytics',
            title: 'Turn sessions and meter values into usable reports',
            description: 'Track charging sessions from start to end, keep transaction detail, and expose energy measurements for reporting, billing, and operational insight.',
            image: sharedImages.sessions,
            alt: 'Illustration of charging session reporting',
            highlights: [
              'Session report with status, transaction details, and energy delivered',
              'Session detail with start/end time, meter values, charge point, and connector',
              'Meter values ready for billing, analytics, and compliance workflows',
            ],
          },
          {
            id: 'payments',
            eyebrow: 'Payments and driver flow',
            title: 'Connect payments without exposing charging complexity',
            description: 'Map terminals to charge points, let drivers scan a QR code, and allow payment or mobility providers to start and stop charging through clean integration contracts.',
            image: sharedImages.payments,
            alt: 'Illustration of a payment terminal and QR flow',
            highlights: [
              'Payment terminal mapping for station and connector context',
              'No-account driver flow: scan, pay, charge',
              'Partner integrations for driver info, connectors, start/stop, and session status',
            ],
          },
          {
            id: 'users',
            eyebrow: 'Secure access',
            title: 'Give each team the right level of access',
            description: 'Operators, admins, and support users sign in once and reach only the work areas and actions their role allows.',
            image: sharedImages.productSecureAccess,
            alt: 'Illustration of secure access for operator teams',
            highlights: [
              'Single sign-on for the operator workspace',
              'User administration for teams and responsibilities',
              'Role-based access for operators, administrators, and protected management features',
            ],
          },
          {
            id: 'diagnostics',
            eyebrow: 'Maintenance and support',
            title: 'Troubleshoot stations from the operator view',
            description: 'Operators can review health, inspect station message history, and run diagnostic actions from the same secured charge point detail flow.',
            image: sharedImages.productDiagnostics,
            alt: 'Illustration of station diagnostics and health checks',
            highlights: [
              'Unified diagnostics under the operator workspace',
              'Health summaries with last seen, faults, connector status, and eviction events',
              'Message history and guided actions for common troubleshooting workflows',
            ],
          },
          {
            id: 'security',
            eyebrow: 'Network protection',
            title: 'Protect the network without blocking integrations',
            description: 'Protect management workflows, keep driver and payment journeys isolated, and ensure only trusted systems participate in charging operations.',
            image: sharedImages.productNetworkProtection,
            alt: 'Illustration of protected charging network connectivity',
            highlights: [
              'Trusted station connections for production deployments',
              'Protected operator and reporting areas',
              'Separate public journeys for payment and driver interactions',
            ],
          },
        ],
      },
      howItWorks: {
        title: 'How it works',
        heroLead:
          'ChargeWhisper coordinates the full charging loop: station connectivity, operator workflows, payment-triggered sessions, driver status, and reporting.',
        introEyebrow: 'Operating model',
        introTitle: 'One platform coordinates the charging loop',
        introBody:
          'ChargeWhisper sits between the physical charging network and every surrounding system. Instead of each actor needing to understand charger behavior, ChargeWhisper keeps the operational source of truth and exposes clear workflows for operators, payment partners, support teams, and drivers.',
        operatingLoop: [
          {
            number: '01',
            title: 'Stations connect',
            description: 'Each charge point identifies itself, reports availability, and keeps ChargeWhisper updated with connector and session state.',
          },
          {
            number: '02',
            title: 'ChargeWhisper keeps the source of truth',
            description: 'The platform stores station status, sessions, meter values, health, terminal mappings, and the operational context around every charging event.',
          },
          {
            number: '03',
            title: 'Systems act through ChargeWhisper',
            description: 'Operators, payment providers, and driver flows trigger actions through controlled product workflows instead of talking to chargers directly.',
          },
          {
            number: '04',
            title: 'Everyone sees the outcome',
            description: 'Operators see reports and diagnostics, drivers see status, and partners can follow the session through a clear integration contract.',
          },
        ],
        lifecycleEyebrow: 'Charging lifecycle',
        lifecycleTitle: 'From station online to completed session',
        lifecycleBody:
          'A charging session is not just a start and stop event. ChargeWhisper follows the entire operational sequence so the network always knows what happened, where it happened, and which data belongs to the session.',
        lifecycle: [
          {
            title: 'Register',
            description: 'A station comes online, ChargeWhisper accepts it, and the network inventory gets the latest status and connector information.',
          },
          {
            title: 'Start',
            description: 'A session starts from an operator action, a payment confirmation, or a driver journey. ChargeWhisper sends the command and tracks the transaction.',
          },
          {
            title: 'Measure',
            description: 'During charging, the station reports session updates and energy readings. ChargeWhisper stores them for reports, billing context, and analytics.',
          },
          {
            title: 'Finish',
            description: 'The session ends, final energy and timestamps are stored, and operators can inspect the completed record later.',
          },
        ],
        driverEyebrow: 'Driver journey',
        driverTitle: 'A no-account path from QR to charging',
        driverBody:
          'Drivers do not need to learn the operator dashboard. They follow a short public journey while ChargeWhisper coordinates the station and payment outcome behind the scenes.',
        driverJourney: [
          {
            title: 'Scan the station QR',
            description: 'The driver opens the public driver page already tied to the correct charge point.',
          },
          {
            title: 'Choose and pay',
            description: 'The payment app shows available connectors, price context, and confirms the payment.',
          },
          {
            title: 'Charging starts',
            description: 'ChargeWhisper starts the session on the selected connector and keeps the operational record updated.',
          },
          {
            title: 'Return to status',
            description: 'The driver comes back to a status page while the operator keeps full visibility in ChargeWhisper.',
          },
        ],
        outcomeEyebrow: 'Why it matters',
        outcomeTitle: 'Every surrounding system gets a simpler job',
        outcomes: [
          {
            title: 'Operators get control',
            description: 'Station state, sessions, health, diagnostics, and reporting stay in one workspace.',
          },
          {
            title: 'Partners get clear contracts',
            description: 'Payment and mobility providers can start, stop, and follow charging without owning station complexity.',
          },
          {
            title: 'Drivers get a short path',
            description: 'The driver journey stays simple: scan, pay, charge, and return to status.',
          },
        ],
      },
      technology: {
        title: 'Technology',
        heroTitle: 'Technology and standards',
        heroLead:
          'The technical foundation behind ChargeWhisper: open charging protocols, secured access, real-time station connectivity, and clean integration contracts.',
        introEyebrow: 'Technical foundation',
        introTitle: 'Built for connected charging infrastructure',
        introBody:
          'ChargeWhisper is designed around open standards and explicit system boundaries. Charge points communicate through a real-time protocol connection, operators and partners use HTTP contracts, and access is controlled through a dedicated identity provider.',
        standardsAriaLabel: 'Core standards and technologies',
        standards: [
          {
            title: 'OCPP',
            description: 'The charge point protocol used for station lifecycle, transactions, meter values, commands, and diagnostics.',
            image: sharedImages.standardOcpp,
            alt: 'OCPP protocol illustration',
          },
          {
            title: 'WebSocket',
            description: 'Persistent station connectivity so charge points can report state and receive commands in real time.',
            image: sharedImages.systemsChargePoints,
            alt: 'Charge point connected to ChargeWhisper over WebSocket',
          },
          {
            title: 'Keycloak',
            description: 'OpenID Connect based authentication, roles, and user administration for operator and admin areas.',
            image: sharedImages.standardKeycloak,
            alt: 'Keycloak identity and roles illustration',
          },
          {
            title: 'mTLS',
            description: 'Optional client-certificate protection for production charge point connections.',
            image: sharedImages.standardMtls,
            alt: 'mTLS certificate shield illustration',
          },
          {
            title: 'REST APIs',
            description: 'HTTP contracts for operator workflows, payment integrations, public driver flows, and administration.',
            image: sharedImages.standardRest,
            alt: 'REST API integrations illustration',
          },
        ],
        ocppEyebrow: 'OCPP coverage',
        ocppTitle: 'Fully OCPP integrated',
        ocppBody:
          'ChargeWhisper is designed around full OCPP integration across station lifecycle, charging sessions, meter values, operational commands, diagnostics, firmware, certificates, and reservations. The product roadmap keeps every charging workflow aligned with the OCPP model.',
        ocppGroups: [
          {
            title: 'Station lifecycle',
            items: ['BootNotification', 'Heartbeat', 'StatusNotification', 'NotifyEvent'],
          },
          {
            title: 'Charging sessions',
            items: ['Authorize', 'TransactionEvent', 'MeterValues', 'RequestStartTransaction', 'RequestStopTransaction'],
          },
          {
            title: 'Operations',
            items: ['Reset', 'ChangeAvailability', 'TriggerMessage', 'GetVariables', 'SetVariables'],
          },
          {
            title: 'Advanced operations',
            items: ['Firmware', 'Certificates', 'Reservations'],
          },
        ],
        transportEyebrow: 'Transport and protection',
        transportTitle: 'Real-time charger connectivity, secured for production',
        transportBody:
          'Charge points connect to ChargeWhisper over WebSocket at a station-specific endpoint. Production deployments can require TLS and client certificates, so only trusted charge points can establish a connection.',
        transportFacts: [
          { label: 'Security option', value: 'TLS + client certificates' },
          { label: 'Runtime behavior', value: 'Persistent connection per charge point' },
        ],
        integrationEyebrow: 'Integration surfaces',
        integrationTitle: 'Separate contracts for different jobs',
        integrationBody:
          'Operator, driver, payment, and admin journeys do not share one generic surface. Each gets a contract with the right authentication and scope.',
        integrationLayers: [
          {
            title: 'Operator APIs',
            description: 'Protected routes for charge point management, sessions, meter values, diagnostics, and remote commands.',
          },
          {
            title: 'Public driver and payment APIs',
            description: 'Dedicated contracts for QR landing, connector lookup, payment-triggered start/stop, and session status.',
          },
          {
            title: 'Administration APIs',
            description: 'User and role operations backed by the identity provider administration client and restricted to admins.',
          },
        ],
      },
      contact: {
        title: 'Contact',
        heroTitle: 'Get in touch',
        heroLead: 'Tell us what you want to build, operate, or integrate. We will help you find the right next step.',
        emailButton: 'Email us',
        responseHint: 'We usually reply within two business days.',
        introEyebrow: 'Contact ChargeWhisper',
        introTitle: 'Start with the outcome you need',
        introBody:
          'Whether you are validating ChargeWhisper for a charging network, preparing a payment integration, or looking for an operator demo, send us a short message and we will route the conversation.',
        pathsAriaLabel: 'Contact options',
        paths: [
          {
            label: 'DEMO',
            title: 'Book a product walkthrough',
            description: 'See how ChargeWhisper supports charge point operations, payments, diagnostics, and driver flows.',
          },
          {
            label: 'SANDBOX',
            title: 'Request evaluation access',
            description: 'Ask for a sandbox environment to test the operator workspace and integration journeys.',
          },
          {
            label: 'PARTNER',
            title: 'Discuss an integration',
            description: 'Bring a payment app, mobility platform, or charging network into the ChargeWhisper flow.',
          },
        ],
        messageEyebrow: 'What to include',
        messageTitle: 'A good first message saves a lot of back and forth',
        messageItems: [
          'Are you operating charge points, evaluating ChargeWhisper, or integrating a partner system?',
          'Approximate number of stations and regions of operation.',
          'Whether you need a demo, sandbox, integration call, or commercial conversation.',
          'Any payment, driver flow, or operational requirement you already know is important.',
        ],
        emailCta: 'Email',
        accessEyebrow: 'Already connected?',
        accessTitle: 'Use the right entry point',
        accessBody:
          'Operators sign in to manage stations and sessions. Drivers do not need an account; they normally arrive through a QR code at the station.',
        loginCta: 'Login as CPO',
        driverCta: 'Open driver app',
      },
    },
  },
  cargaclara: {
    key: 'cargaclara',
    locale: 'es-AR',
    htmlLang: 'es-AR',
    name: 'Carga Clara',
    shortName: 'Carga Clara',
    mark: 'C',
    domain: 'cargaclara.com.ar',
    tagline: 'Soporte inteligente para puntos de carga.',
    titleBase: 'Carga Clara - Diagnostico inteligente para puntos de carga',
    footer: 'Carga Clara. Diagnostico y operacion de puntos de carga OCPP.',
    seo: {
      title: 'Carga Clara - Diagnostico inteligente para puntos de carga',
      description:
        'Diagnostico y soporte con IA para operadores de puntos de carga electrica con OCPP, logs de mensajes, salud del cargador y flujos operativos.',
      twitterDescription: 'Soporte inteligente para puntos de carga OCPP.',
    },
    nav: {
      product: 'Producto',
      howItWorks: 'Como funciona',
      technology: 'Tecnologia',
      contact: 'Contacto',
      requestDemo: 'Solicitar demo',
      login: 'Ingresar',
      driverApp: 'App conductores',
    },
    cta: {
      eyebrow: 'Listo para operar?',
      heading: 'Opera tu red de carga con Carga Clara',
      subheading:
        'Ingresa si ya tenes acceso, o solicita una demo para ver como Carga Clara conecta puntos de carga, pagos, diagnostico y flujos para conductores.',
      loginAsCpo: 'Ingresar como CPO',
      requestDemo: 'Solicitar demo',
      driverLink: 'Conductor? Abrir app',
    },
    contact: {
      email: 'hola@cargaclara.com.ar',
      subject: 'Carga Clara - Solicitud de demo o acceso',
    },
    diagram: {
      ariaLabel:
        'Ecosistema de Carga Clara: los puntos de carga se conectan al backend de Carga Clara por OCPP; el backend expone APIs REST a frontends de operador y conductor y a sistemas de pago.',
      chargePoints: 'Puntos de carga',
      drivers: 'Conductores',
      driverSub: 'Escanear QR / pagar / cargar',
      backend: 'Backend Carga Clara',
      operatorFrontend: 'Frontend operador',
      paymentSystems: 'Sistemas de pago',
      driverApp: 'App conductores',
      qrLanding: 'Landing QR',
    },
    pages: {
      home: {
        title: undefined,
        eyebrow: 'Carga Clara',
        heroTitle: 'Una capa de control para tu red de carga electrica',
        heroLead:
          'Carga Clara conecta puntos de carga, operadores, pagos, diagnostico y flujos para conductores en una plataforma central. Habla OCPP con los cargadores, expone APIs para integraciones y le da al operador la visibilidad necesaria para sostener una infraestructura confiable.',
        heroImage: sharedImages.overview,
        heroImageAlt: 'Carga Clara conectando puntos de carga, pagos, operadores y conductores',
        pillarsHeading: 'Del enchufe al pago, en una sola plataforma',
        pillarsSubhead: 'Cuatro pilares para cubrir todo el ciclo de vida de cada sesion de carga.',
        pillars: [
          {
            title: 'Gestion de puntos de carga',
            description: 'Registra y monitorea estaciones, estado en tiempo real, resumenes de salud y comandos OCPP remotos desde un unico tablero.',
            image: sharedImages.chargePoints,
            alt: 'Ilustracion de estaciones de carga electrica con un cable conector',
            link: { to: '/product#management', label: 'Ver funcionalidad' },
          },
          {
            title: 'Sesiones y reportes',
            description: 'Reporte de sesiones, detalle de transacciones y valores de medidor para facturacion, analitica y cumplimiento.',
            image: sharedImages.sessions,
            alt: 'Ilustracion de un grafico de sesiones de carga en kilowatt-hora',
            link: { to: '/product#sessions', label: 'Ver funcionalidad' },
          },
          {
            title: 'Integracion de pagos',
            description: 'Mapea terminales a puntos de carga, inicia y detiene transacciones via pago y ofrece a conductores un flujo QR sin cuenta.',
            image: sharedImages.payments,
            alt: 'Ilustracion de una terminal de pago junto a un codigo QR',
            link: { to: '/product#payments', label: 'Ver funcionalidad' },
          },
          {
            title: 'Diagnostico y mantenimiento',
            description: 'Logs de mensajes OCPP, visibilidad de salud, eventos de desconexion y comandos de diagnostico por punto de carga.',
            image: sharedImages.diagnostics,
            alt: 'Ilustracion de una consola de logs OCPP',
            link: { to: '/product#diagnostics', label: 'Ver funcionalidad' },
          },
        ],
        carousel: {
          slideLabel: 'Diapositiva',
          summarySlide: 'Ver todas las funcionalidades',
          previous: 'Anterior',
          next: 'Siguiente',
        },
        pillarsSummaryTitle: 'Ver el panorama completo',
        pillarsSummaryDescription: 'Tablero, sesiones, pagos, diagnostico y seguridad: explora todas las capacidades en un solo lugar.',
        pillarsSummaryCta: 'Ver todas las funcionalidades',
        systemsHeading: 'Conecta cada parte de tu operacion de carga',
        systemsSubhead:
          'Carga Clara se ubica entre tus estaciones, tu equipo, tus pagos y tus conductores para que todos los sistemas hablen el mismo idioma.',
        systemsLink: 'Ver la arquitectura y los flujos completos',
        systems: [
          {
            title: 'Habla con cada punto de carga',
            body: 'Las estaciones se conectan por OCPP sobre WebSocket. Carga Clara mantiene una sesion activa por punto de carga, recibe eventos de inicio, estado y transaccion, y envia comandos remotos como iniciar, detener y reiniciar. En produccion puede correr con mTLS para que solo tu hardware se conecte.',
            image: sharedImages.systemsChargePoints,
            alt: 'Punto de carga intercambiando mensajes OCPP con el backend de Carga Clara',
          },
          {
            title: 'Conecta el proveedor de pagos que ya usas',
            body: 'Mapea terminales a cargadores, ofrece un flujo QR sin cuenta para conductores y permite que cualquier app de pago compatible inicie y detenga sesiones por APIs publicas. Tu proveedor de terminales no necesita conocer OCPP.',
            image: sharedImages.systemsPayments,
            alt: 'Conductor escanea un QR, la app de pago cobra y Carga Clara inicia la sesion',
          },
          {
            title: 'Una identidad para tu equipo',
            body: 'Keycloak maneja autenticacion, roles y administracion de usuarios. Los operadores ingresan una vez y acceden al tablero, reportes y diagnostico con los permisos correctos, y podes federar Keycloak con el proveedor de identidad que ya uses.',
            image: sharedImages.systemsIdentity,
            alt: 'Tres integrantes del equipo ingresando una vez con roles de Keycloak',
          },
        ],
        standardsHeading: 'Construido sobre estandares abiertos',
        standardsLink: 'Mas sobre la tecnologia',
        standards: [
          {
            title: 'OCPP',
            description: 'Comunicacion nativa de protocolo entre Carga Clara y cada punto de carga conectado.',
            image: sharedImages.standardOcpp,
            alt: 'Mensajes del protocolo OCPP conectando un punto de carga con Carga Clara',
          },
          {
            title: 'Keycloak',
            description: 'Autenticacion central, roles y control de acceso para operadores.',
            image: sharedImages.standardKeycloak,
            alt: 'Llave de identidad conectando usuarios y roles',
          },
          {
            title: 'mTLS',
            description: 'Proteccion opcional con certificados para conexiones productivas de puntos de carga.',
            image: sharedImages.standardMtls,
            alt: 'Escudo de certificado protegiendo una conexion de red de carga',
          },
          {
            title: 'REST APIs',
            description: 'Capa de integracion clara para operadores, sistemas de pago y flujos de conductores.',
            image: sharedImages.standardRest,
            alt: 'Nodos REST API intercambiando solicitudes de integracion',
          },
        ],
        audiencesHeading: 'Quienes usan Carga Clara',
        audiencesSubhead: 'Pensado para los equipos que operan, integran, mantienen y usan redes de carga electrica.',
        audiences: [
          {
            label: 'CPO',
            title: 'Operadores de puntos de carga',
            description: 'Operan la red desde un lugar: estaciones, estado en vivo, sesiones, mediciones y comandos remotos.',
          },
          {
            label: 'SITE',
            title: 'Duenos de sitios y flotas',
            description: 'Siguen uptime, salud del cargador, energia entregada y configuracion de pagos por ubicacion.',
          },
          {
            label: 'PAY',
            title: 'Proveedores de pago y movilidad',
            description: 'Conectan apps de pago por APIs publicas para flujo QR, inicio/detencion y estado de sesion.',
          },
          {
            label: 'OPS',
            title: 'Equipos de mantenimiento',
            description: 'Resuelven problemas con resumenes de salud, logs OCPP y comandos de diagnostico.',
          },
          {
            label: 'ADM',
            title: 'Administradores',
            description: 'Gestionan usuarios, roles, accesos, mapeo de terminales y permisos respaldados por Keycloak.',
          },
          {
            label: 'DRV',
            title: 'Conductores',
            description: 'Escanean, pagan y cargan sin crear una cuenta en Carga Clara ni aprender una app nueva.',
          },
        ],
        driverHeading: 'Flujo del conductor de un vistazo',
        driverSubhead: 'Un recorrido simple desde el QR de la estacion hasta una sesion de carga activa.',
        driverLink: 'Ver el flujo completo del conductor',
        driverSteps: [
          {
            number: '01',
            title: 'Escanear',
            description: 'El conductor escanea el codigo QR de la estacion y abre la app con el id del punto de carga.',
          },
          {
            number: '02',
            title: 'Pagar',
            description: 'La app de pago configurada muestra conectores disponibles e inicia la sesion despues del pago.',
          },
          {
            number: '03',
            title: 'Cargar',
            description: 'El conductor vuelve a una pagina de estado mientras Carga Clara sigue la sesion y la energia entregada.',
          },
        ],
      },
      product: {
        title: 'Producto',
        heroLead:
          'Todo lo que Carga Clara te da para operar una red de carga electrica: gestion, sesiones, pagos, diagnostico y seguridad.',
        introEyebrow: 'Capacidades del producto',
        introTitle: 'Todo lo necesario para operar una red de carga',
        introBody:
          'Carga Clara une operacion de red, reportes de carga, integraciones de pago, diagnostico y acceso seguro en un producto para operadores. Conecta estaciones, equipos, proveedores de pago y recorridos de conductores con flujos claros.',
        navAriaLabel: 'Capacidades del producto',
        sections: [
          {
            id: 'management',
            eyebrow: 'Operacion de red',
            title: 'Controla cada punto de carga desde un solo lugar',
            description: 'Registra estaciones, monitorea estado en vivo, inspecciona disponibilidad de conectores y envia comandos remotos sin cambiar de herramienta.',
            image: sharedImages.chargePoints,
            alt: 'Ilustracion de puntos de carga electrica gestionados',
            highlights: [
              'Registro de puntos de carga con EVSEs y conectores',
              'Estados online, offline, disponible, ocupado, con falla y no disponible',
              'Comandos remotos para iniciar, detener, desbloquear, reiniciar, variables, disponibilidad y perfiles de red',
            ],
          },
          {
            id: 'sessions',
            eyebrow: 'Facturacion y analitica',
            title: 'Convierte sesiones y mediciones en reportes utiles',
            description: 'Sigue sesiones desde el inicio hasta el fin, guarda detalle de transaccion y expone mediciones de energia para reportes, facturacion e insight operativo.',
            image: sharedImages.sessions,
            alt: 'Ilustracion de reportes de sesiones de carga',
            highlights: [
              'Reporte de sesiones con estado, detalle de transaccion y energia entregada',
              'Detalle de sesion con inicio, fin, valores de medidor, punto de carga y conector',
              'Mediciones listas para facturacion, analitica y cumplimiento',
            ],
          },
          {
            id: 'payments',
            eyebrow: 'Pagos y flujo de conductor',
            title: 'Conecta pagos sin exponer la complejidad de carga',
            description: 'Mapea terminales a puntos de carga, permite que conductores escaneen un QR y deja que proveedores de pago o movilidad inicien y detengan cargas mediante contratos claros.',
            image: sharedImages.payments,
            alt: 'Ilustracion de una terminal de pago y flujo QR',
            highlights: [
              'Mapeo de terminales de pago con contexto de estacion y conector',
              'Flujo sin cuenta para conductores: escanear, pagar, cargar',
              'Integraciones para informacion al conductor, conectores, inicio/detencion y estado de sesion',
            ],
          },
          {
            id: 'users',
            eyebrow: 'Acceso seguro',
            title: 'Dale a cada equipo el nivel de acceso correcto',
            description: 'Operadores, admins y usuarios de soporte ingresan una vez y acceden solo a las areas y acciones permitidas por su rol.',
            image: sharedImages.productSecureAccess,
            alt: 'Ilustracion de acceso seguro para equipos operadores',
            highlights: [
              'Inicio de sesion unico para el workspace operador',
              'Administracion de usuarios para equipos y responsabilidades',
              'Acceso basado en roles para operadores, administradores y funciones protegidas',
            ],
          },
          {
            id: 'diagnostics',
            eyebrow: 'Mantenimiento y soporte',
            title: 'Diagnostica estaciones desde la vista del operador',
            description: 'Los operadores pueden revisar salud, inspeccionar historial de mensajes y ejecutar acciones de diagnostico desde el mismo detalle seguro del punto de carga.',
            image: sharedImages.productDiagnostics,
            alt: 'Ilustracion de diagnostico y salud de estaciones',
            highlights: [
              'Diagnostico unificado dentro del workspace operador',
              'Resumenes de salud con ultimo contacto, fallas, estado de conectores y eventos de desconexion',
              'Historial de mensajes y acciones guiadas para problemas comunes',
            ],
          },
          {
            id: 'security',
            eyebrow: 'Proteccion de red',
            title: 'Protege la red sin bloquear integraciones',
            description: 'Protege flujos de gestion, mantiene aislados los recorridos de pago y conductor, y asegura que solo sistemas confiables participen en la operacion de carga.',
            image: sharedImages.productNetworkProtection,
            alt: 'Ilustracion de conectividad protegida para red de carga',
            highlights: [
              'Conexiones confiables de estaciones para despliegues productivos',
              'Areas protegidas de operacion y reportes',
              'Flujos publicos separados para pagos e interacciones de conductores',
            ],
          },
        ],
      },
      howItWorks: {
        title: 'Como funciona',
        heroLead:
          'Carga Clara coordina todo el ciclo de carga: conectividad de estaciones, flujos de operador, sesiones iniciadas por pago, estado para conductores y reportes.',
        introEyebrow: 'Modelo operativo',
        introTitle: 'Una plataforma coordina el ciclo de carga',
        introBody:
          'Carga Clara se ubica entre la red fisica de carga y cada sistema alrededor. En vez de que cada actor entienda el comportamiento del cargador, Carga Clara mantiene la fuente operativa de verdad y expone flujos claros para operadores, partners de pago, soporte y conductores.',
        operatingLoop: [
          {
            number: '01',
            title: 'Las estaciones se conectan',
            description: 'Cada punto de carga se identifica, reporta disponibilidad y mantiene a Carga Clara actualizado con estado de conectores y sesiones.',
          },
          {
            number: '02',
            title: 'Carga Clara mantiene la fuente de verdad',
            description: 'La plataforma guarda estado de estaciones, sesiones, valores de medidor, salud, mapeo de terminales y contexto operativo de cada evento de carga.',
          },
          {
            number: '03',
            title: 'Los sistemas actuan mediante Carga Clara',
            description: 'Operadores, proveedores de pago y flujos de conductor ejecutan acciones por flujos controlados en vez de hablar directo con los cargadores.',
          },
          {
            number: '04',
            title: 'Todos ven el resultado',
            description: 'Los operadores ven reportes y diagnostico, los conductores ven estado y los partners siguen la sesion mediante un contrato claro.',
          },
        ],
        lifecycleEyebrow: 'Ciclo de carga',
        lifecycleTitle: 'Desde estacion online hasta sesion finalizada',
        lifecycleBody:
          'Una sesion de carga no es solo un inicio y una detencion. Carga Clara sigue toda la secuencia operativa para que la red siempre sepa que paso, donde paso y que datos pertenecen a la sesion.',
        lifecycle: [
          {
            title: 'Registrar',
            description: 'Una estacion se conecta, Carga Clara la acepta y el inventario de red recibe el ultimo estado e informacion de conectores.',
          },
          {
            title: 'Iniciar',
            description: 'Una sesion inicia por accion del operador, confirmacion de pago o recorrido de conductor. Carga Clara envia el comando y sigue la transaccion.',
          },
          {
            title: 'Medir',
            description: 'Durante la carga, la estacion reporta actualizaciones y energia. Carga Clara las guarda para reportes, facturacion y analitica.',
          },
          {
            title: 'Finalizar',
            description: 'La sesion termina, se guardan energia final y horarios, y los operadores pueden inspeccionar el registro luego.',
          },
        ],
        driverEyebrow: 'Recorrido del conductor',
        driverTitle: 'Un camino sin cuenta desde el QR hasta la carga',
        driverBody:
          'Los conductores no necesitan aprender el tablero operador. Siguen un recorrido publico y corto mientras Carga Clara coordina la estacion y el resultado de pago por detras.',
        driverJourney: [
          {
            title: 'Escanear el QR de la estacion',
            description: 'El conductor abre la pagina publica ya vinculada al punto de carga correcto.',
          },
          {
            title: 'Elegir y pagar',
            description: 'La app de pago muestra conectores disponibles, contexto de precio y confirma el pago.',
          },
          {
            title: 'La carga comienza',
            description: 'Carga Clara inicia la sesion en el conector seleccionado y mantiene actualizado el registro operativo.',
          },
          {
            title: 'Volver al estado',
            description: 'El conductor vuelve a una pagina de estado mientras el operador mantiene visibilidad completa en Carga Clara.',
          },
        ],
        outcomeEyebrow: 'Por que importa',
        outcomeTitle: 'Cada sistema alrededor tiene un trabajo mas simple',
        outcomes: [
          {
            title: 'Los operadores ganan control',
            description: 'Estado de estaciones, sesiones, salud, diagnostico y reportes quedan en un solo workspace.',
          },
          {
            title: 'Los partners reciben contratos claros',
            description: 'Proveedores de pago y movilidad pueden iniciar, detener y seguir cargas sin hacerse cargo de la complejidad de estaciones.',
          },
          {
            title: 'Los conductores tienen un camino corto',
            description: 'El recorrido se mantiene simple: escanear, pagar, cargar y volver al estado.',
          },
        ],
      },
      technology: {
        title: 'Tecnologia',
        heroTitle: 'Tecnologia y estandares',
        heroLead:
          'La base tecnica de Carga Clara: protocolos abiertos de carga, acceso seguro, conectividad en tiempo real e integraciones claras.',
        introEyebrow: 'Base tecnica',
        introTitle: 'Construido para infraestructura de carga conectada',
        introBody:
          'Carga Clara esta disenado alrededor de estandares abiertos y limites de sistema explicitos. Los puntos de carga se comunican por una conexion de protocolo en tiempo real, operadores y partners usan contratos HTTP, y el acceso se controla con un proveedor de identidad dedicado.',
        standardsAriaLabel: 'Estandares y tecnologias principales',
        standards: [
          {
            title: 'OCPP',
            description: 'El protocolo de puntos de carga usado para ciclo de estacion, transacciones, mediciones, comandos y diagnostico.',
            image: sharedImages.standardOcpp,
            alt: 'Ilustracion del protocolo OCPP',
          },
          {
            title: 'WebSocket',
            description: 'Conectividad persistente para que los puntos de carga reporten estado y reciban comandos en tiempo real.',
            image: sharedImages.systemsChargePoints,
            alt: 'Punto de carga conectado a Carga Clara por WebSocket',
          },
          {
            title: 'Keycloak',
            description: 'Autenticacion basada en OpenID Connect, roles y administracion de usuarios para areas de operador y admin.',
            image: sharedImages.standardKeycloak,
            alt: 'Ilustracion de identidad y roles con Keycloak',
          },
          {
            title: 'mTLS',
            description: 'Proteccion opcional con certificados de cliente para conexiones productivas de puntos de carga.',
            image: sharedImages.standardMtls,
            alt: 'Ilustracion de escudo de certificado mTLS',
          },
          {
            title: 'REST APIs',
            description: 'Contratos HTTP para flujos de operador, integraciones de pago, flujos publicos de conductor y administracion.',
            image: sharedImages.standardRest,
            alt: 'Ilustracion de integraciones REST API',
          },
        ],
        ocppEyebrow: 'Cobertura OCPP',
        ocppTitle: 'Integrado completamente con OCPP',
        ocppBody:
          'Carga Clara esta disenado alrededor de una integracion completa OCPP para ciclo de estaciones, sesiones de carga, mediciones, comandos operativos, diagnostico, firmware, certificados y reservas. La hoja de ruta mantiene cada flujo de carga alineado con el modelo OCPP.',
        ocppGroups: [
          {
            title: 'Ciclo de estacion',
            items: ['BootNotification', 'Heartbeat', 'StatusNotification', 'NotifyEvent'],
          },
          {
            title: 'Sesiones de carga',
            items: ['Authorize', 'TransactionEvent', 'MeterValues', 'RequestStartTransaction', 'RequestStopTransaction'],
          },
          {
            title: 'Operaciones',
            items: ['Reset', 'ChangeAvailability', 'TriggerMessage', 'GetVariables', 'SetVariables'],
          },
          {
            title: 'Operaciones avanzadas',
            items: ['Firmware', 'Certificates', 'Reservations'],
          },
        ],
        transportEyebrow: 'Transporte y proteccion',
        transportTitle: 'Conectividad en tiempo real, segura para produccion',
        transportBody:
          'Los puntos de carga se conectan a Carga Clara por WebSocket en un endpoint especifico por estacion. Los despliegues productivos pueden requerir TLS y certificados de cliente para que solo puntos de carga confiables establezcan conexion.',
        transportFacts: [
          { label: 'Opcion de seguridad', value: 'TLS + certificados de cliente' },
          { label: 'Comportamiento runtime', value: 'Conexion persistente por punto de carga' },
        ],
        integrationEyebrow: 'Superficies de integracion',
        integrationTitle: 'Contratos separados para trabajos diferentes',
        integrationBody:
          'Los recorridos de operador, conductor, pago y admin no comparten una superficie generica. Cada uno recibe un contrato con autenticacion y alcance adecuados.',
        integrationLayers: [
          {
            title: 'APIs de operador',
            description: 'Rutas protegidas para gestion de puntos de carga, sesiones, mediciones, diagnostico y comandos remotos.',
          },
          {
            title: 'APIs publicas de conductor y pago',
            description: 'Contratos dedicados para landing QR, consulta de conectores, inicio/detencion por pago y estado de sesion.',
          },
          {
            title: 'APIs de administracion',
            description: 'Operaciones de usuarios y roles respaldadas por el cliente admin del proveedor de identidad y restringidas a admins.',
          },
        ],
      },
      contact: {
        title: 'Contacto',
        heroTitle: 'Hablemos',
        heroLead: 'Contanos que queres construir, operar o integrar. Te ayudamos a encontrar el proximo paso correcto.',
        emailButton: 'Escribinos',
        responseHint: 'Normalmente respondemos dentro de dos dias habiles.',
        introEyebrow: 'Contacto Carga Clara',
        introTitle: 'Empeza por el resultado que necesitas',
        introBody:
          'Si estas evaluando Carga Clara para una red de carga, preparando una integracion de pago o buscando una demo para operadores, envianos un mensaje corto y encaminamos la conversacion.',
        pathsAriaLabel: 'Opciones de contacto',
        paths: [
          {
            label: 'DEMO',
            title: 'Coordinar una demo del producto',
            description: 'Mira como Carga Clara acompana la operacion de puntos de carga, pagos, diagnostico y flujos para conductores.',
          },
          {
            label: 'SANDBOX',
            title: 'Solicitar acceso de evaluacion',
            description: 'Pedi un entorno sandbox para probar el workspace operador y los recorridos de integracion.',
          },
          {
            label: 'PARTNER',
            title: 'Conversar una integracion',
            description: 'Integra una app de pago, plataforma de movilidad o red de carga al flujo de Carga Clara.',
          },
        ],
        messageEyebrow: 'Que incluir',
        messageTitle: 'Un buen primer mensaje ahorra varias idas y vueltas',
        messageItems: [
          'Si operas puntos de carga, estas evaluando Carga Clara o integrando un sistema partner.',
          'Cantidad aproximada de estaciones y regiones de operacion.',
          'Si necesitas demo, sandbox, llamada de integracion o conversacion comercial.',
          'Cualquier requisito de pago, flujo de conductor u operacion que ya sepas que importa.',
        ],
        emailCta: 'Email',
        accessEyebrow: 'Ya estas conectado?',
        accessTitle: 'Usa el punto de entrada correcto',
        accessBody:
          'Los operadores ingresan para gestionar estaciones y sesiones. Los conductores no necesitan cuenta; normalmente llegan por un QR en la estacion.',
        loginCta: 'Ingresar como CPO',
        driverCta: 'Abrir app de conductores',
      },
    },
  },
};

export function getBrandContent(brandKey) {
  return BRAND_CONTENT[brandKey] || BRAND_CONTENT[DEFAULT_BRAND_KEY];
}
